// libraries.
import { useFormContext } from 'react-hook-form';
import { Form, InputGroup } from 'react-bootstrap';

// styles.
import { WrapperInputField } from './styles';
import { ConfidenceLevel } from '@domains/documents-review/hooks/useFieldConfidence';
import { withRequiredMark } from '@shared/utils/fieldLabel';

interface InputFieldProps {
  label: string;
  maxLength?: number;
  name: string;
  pattern?: RegExp;
  placeholder?: string;
  required?: boolean;
  subLabel?: string;
  type?: string;
  disabled?: boolean;
  confidenceLevel?: ConfidenceLevel;
  confidenceBorderColor?: string;
  confidenceBoxShadow?: string;
  onFieldEdit?: (fieldName: string) => void;
}

export const InputField = ({
  label,
  maxLength,
  name,
  pattern,
  placeholder = '',
  type = 'text',
  required = false,
  subLabel,
  disabled = false,
  confidenceLevel,
  confidenceBorderColor,
  confidenceBoxShadow,
  onFieldEdit,
}: InputFieldProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const validationRules = {
    ...(required && { required: 'Campo requerido' }),
    ...(pattern && { pattern }),
  };

  const confidenceStyle =
    confidenceLevel && confidenceBorderColor && confidenceBoxShadow
      ? {
          border: `1px solid ${confidenceBorderColor}`,
          boxShadow: confidenceBoxShadow,
        }
      : {};

  // Disable field if confidence level is HIGH or if explicitly disabled
  const isDisabled = disabled || confidenceLevel === 'HIGH';

  // Get register props and extend onChange
  const { onChange: registerOnChange, ...registerProps } = register(
    name,
    validationRules
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Call the onFieldEdit callback when user edits
    if (onFieldEdit) {
      onFieldEdit(name);
    }
    // Call original react-hook-form onChange
    return registerOnChange(e);
  };

  return (
    <WrapperInputField>
      <Form.Group>
        <Form.Label htmlFor={name}>{withRequiredMark(label, required)}</Form.Label>
        <InputGroup>
          <Form.Control
            id={name}
            maxLength={maxLength}
            disabled={isDisabled}
            type={type}
            placeholder={placeholder}
            style={confidenceStyle}
            onChange={handleChange}
            {...registerProps}
          />
          {subLabel && <InputGroup.Text>{subLabel}</InputGroup.Text>}
        </InputGroup>
      </Form.Group>
      <div className="d-flex justify-content-center">
        {errors[name] && (
          <small className="text-danger my-1 fw-bold">
            {errors[name]?.message as string}
          </small>
        )}
      </div>
    </WrapperInputField>
  );
};
